export class HttpStatusError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'HttpStatusError'
  }
}

export class RateLimitExceededError extends Error {
  constructor(
    public readonly status: number,
    public readonly retryAfterMs: number,
  ) {
    super(`Rate limit hit (status ${status}); retry after ${retryAfterMs}ms`)
    this.name = 'RateLimitExceededError'
  }
}

function parseRetryAfterMs(header: string | null): number {
  if (!header) return 0

  const deltaSeconds = Number(header)
  if (!isNaN(deltaSeconds) && isFinite(deltaSeconds)) {
    return Math.max(0, deltaSeconds) * 1000
  }

  const date = new Date(header)
  if (!isNaN(date.getTime())) {
    return Math.max(0, date.getTime() - Date.now())
  }

  return 0
}

export const assertStatus = {
  lessThan(threshold: number, response?: Response) {
    function assertStatusLessThan(res: Response) {
      if (res.status >= threshold) {
        throw new HttpStatusError(
          res.status,
          `HTTP ${res.status} (${res.statusText})\nexpected status code less than ${threshold}`,
        )
      }
      return res
    }

    if (response) {
      assertStatusLessThan(response)
    }

    return assertStatusLessThan
  },

  ok(response?: Response) {
    function assertStatusOk(res: Response) {
      if (!res.ok) {
        throw new HttpStatusError(
          res.status,
          `HTTP ${res.status} (${res.statusText})\nexpected status code 2xx`,
        )
      }
      return res
    }

    if (response) {
      assertStatusOk(response)
    }

    return assertStatusOk
  },

  withinRateLimit(response?: Response) {
    function assertWithinRateLimit(res: Response) {
      if (res.status === 429 || res.status === 503) {
        throw new RateLimitExceededError(
          res.status,
          parseRetryAfterMs(res.headers.get('Retry-After')),
        )
      }
      return res
    }

    if (response) {
      assertWithinRateLimit(response)
    }

    return assertWithinRateLimit
  },
}
